// src/App.js
import React, { Suspense, lazy } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import PrelaunchGate from "./components/PrelaunchGate";
import EntryVerificationGate from "./components/EntryVerificationGate";
import AppErrorFallback from "./components/AppErrorFallback/AppErrorFallback";
import MessagingRouteGate from "../features/entitlements/components/MessagingRouteGate";
import {
  installClientDiagnostics,
  reportClientError,
} from "../shared/diagnostics/clientDiagnostics";
import MeetTheTeam from "../features/public/pages/MeetTheTeam";

installClientDiagnostics();

export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: null, errorPath: null };
  }

  static getDerivedStateFromError(error) {
    let isLoggedIn = false;
    try {
      isLoggedIn = Object.keys(globalThis.localStorage || {}).some(
        (k) => k.startsWith("sb-") && k.endsWith("-auth-token"),
      );
    } catch (e) {
      console.warn("[AppErrorBoundary] Could not read localStorage:", e);
    }
    return {
      hasError: true,
      errorMessage: error?.message || "Unknown error",
      errorPath: globalThis.location?.pathname || "/",
      isLoggedIn,
    };
  }

  componentDidCatch(error, info) {
    console.error("[AppErrorBoundary] Uncaught render error:", error, info);
    void reportClientError(error, {
      source: "react_error_boundary",
      componentStack: info?.componentStack || null,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <AppErrorFallback
          errorMessage={this.state.errorMessage}
          errorPath={this.state.errorPath}
          isLoggedIn={this.state.isLoggedIn}
        />
      );
    }
    return this.props.children;
  }
}

export function RouteLoadingFallback() {
  return (
    <div
      aria-live="polite"
      aria-label="Loading page"
      className="route-loading-bar"
      role="status"
    >
      <span className="sr-only">Loading page</span>
    </div>
  );
}

const PublicLayout = lazy(() => import("./layouts/PublicLayout"));
const InternalLayout = lazy(() => import("./layouts/InternalLayout"));
const LoggedOutLayout = lazy(() => import("./layouts/LoggedOutLayout"));
const FullscreenLayout = lazy(() => import("./layouts/FullscreenLayout"));
const AppDataShell = lazy(() => import("./layouts/AppDataShell"));
const PrivateAppShell = lazy(() => import("./layouts/PrivateAppShell"));
const AdminLayout = lazy(() => import("../features/admin/layout/AdminLayout"));

const PublicHomeRoute = lazy(() => import("../features/public/pages/Home"));
const Features = lazy(() => import("../features/public/pages/Features"));
const Pricing = lazy(() => import("../features/public/pages/Pricing"));
const WhyRefanno = lazy(() => import("../features/public/pages/WhyRefanno"));
const Contact = lazy(() => import("../features/public/pages/Contact"));
const ContactSales = lazy(
  () => import("../features/public/pages/ContactSales"),
);
const FAQ = lazy(() => import("../features/public/pages/FAQ"));
const GettingStarted = lazy(() => import("../features/public/pages/GettingStarted"));
const Glossary = lazy(() => import("../features/public/pages/Glossary"));
const Terms = lazy(() => import("../features/public/pages/Terms"));
const Privacy = lazy(() => import("../features/public/pages/Privacy"));
const EarlyAccessQuestionnaire = lazy(
  () => import("../features/public/pages/EarlyAccessQuestionnaire"),
);

const AuthStart = lazy(() => import("../features/auth/pages/AuthStart"));
const AuthCallback = lazy(() => import("../features/auth/pages/AuthCallback"));
const AuthComplete = lazy(() => import("../features/auth/pages/AuthComplete"));
const Login = lazy(() => import("../features/auth/pages/Login"));
const MagicLinkLogin = lazy(
  () => import("../features/auth/pages/MagicLinkLogin"),
);
const Signup = lazy(() => import("../features/auth/pages/Signup"));
const SignupComplete = lazy(
  () => import("../features/auth/pages/SignupComplete"),
);
const ResetPassword = lazy(
  () => import("../features/auth/pages/ResetPassword"),
);
const Welcome = lazy(() => import("../features/auth/pages/Welcome"));
const EnterpriseCheckoutClaim = lazy(
  () => import("../features/auth/pages/EnterpriseCheckoutClaim"),
);
const Invite = lazy(() => import("../features/auth/pages/Invite"));
const AccessRemoved = lazy(
  () => import("../features/auth/pages/AccessRemoved"),
);
const AccessBlocked = lazy(
  () => import("../features/auth/pages/AccessBlocked"),
);
const SsoRequired = lazy(
  () => import("../features/auth/pages/SsoRequired"),
);
const EmailChangePending = lazy(
  () => import("../features/auth/pages/EmailChangePending"),
);
const PendingEmailConfirmation = lazy(
  () => import("../features/auth/pages/PendingEmailConfirmation"),
);
const PendingInvite = lazy(
  () => import("../features/auth/pages/PendingInvite"),
);
const Logout = lazy(() => import("../features/auth/pages/Logout"));

const Dashboard = lazy(() => import("../features/dashboard/pages/Dashboard"));
const Viewer = lazy(
  () => import("../features/reference-documents/viewer/Viewer"),
);
const Assets = lazy(() => import("../features/assets/pages/Assets"));
const AssetApprovals = lazy(
  () => import("../features/assets/pages/AssetApprovals"),
);
const ReviewWorkspace = lazy(
  () => import("../features/review/pages/ReviewWorkspace"),
);
const ExternalReviewerInvitePage = lazy(
  () => import("../features/external-review/pages/ExternalReviewerInvitePage"),
);
const ExternalReviewerWorkspace = lazy(
  () => import("../features/external-review/pages/ExternalReviewerWorkspace"),
);
const ExternalReviewerAdminPage = lazy(
  () => import("../features/external-review/pages/ExternalReviewerAdminPage"),
);
const AssetEditor = lazy(() => import("../features/assets/pages/AssetEditor"));
const Portfolio = lazy(() => import("../features/portfolio/pages/Portfolio"));
const ProjectDetail = lazy(
  () => import("../features/projects/pages/ProjectDetail"),
);
const ReferenceLibrary = lazy(
  () => import("../features/reference-documents/library/ReferenceLibrary"),
);
const BrandClaims = lazy(
  () => import("../features/brand-claims/pages/BrandClaims"),
);
const BrandClaimBuilder = lazy(
  () => import("../features/brand-claims/pages/BrandClaimBuilder"),
);
const BrandClaimApprovals = lazy(
  () => import("../features/brand-claims/pages/BrandClaimApprovals"),
);
const Settings = lazy(() => import("../features/user-settings/pages/Settings"));
const InboxPage = lazy(() => import("../features/messages/pages/Inbox"));
const Help = lazy(() => import("../features/help/pages/Help"));
const BillingPortal = lazy(
  () => import("../features/payments/pages/BillingPortal"),
);
const ChangeHistory = lazy(
  () => import("../features/change-history/pages/ChangeHistory"),
);
const Archive = lazy(() => import("../features/archive/pages/Archive"));
const ExportsPage = lazy(() => import("../features/exports/pages/ExportsPage"));
const AdminOverview = lazy(
  () => import("../features/admin/pages/AdminOverview"),
);
const AdminSupportRequests = lazy(
  () => import("../features/admin/pages/AdminSupportRequests"),
);
const AdminContactRequests = lazy(
  () => import("../features/admin/pages/AdminContactRequests"),
);
const AdminEnterpriseContracts = lazy(
  () => import("../features/admin/pages/AdminEnterpriseContracts"),
);
const AdminOrganizations = lazy(
  () => import("../features/admin/pages/AdminOrganizations"),
);
const AdminOrganizationDetail = lazy(
  () => import("../features/admin/pages/AdminOrganizationDetail"),
);
const AdminOrganizationSecurity = lazy(
  () => import("../features/admin/pages/AdminOrganizationSecurity"),
);
const AdminUsers = lazy(() => import("../features/admin/pages/AdminUsers"));
const AdminUserDetail = lazy(
  () => import("../features/admin/pages/AdminUserDetail"),
);
const AdminTeams = lazy(() => import("../features/admin/pages/AdminTeams"));
const AdminTeamDetail = lazy(
  () => import("../features/admin/pages/AdminTeamDetail"),
);
const AdminContent = lazy(() => import("../features/admin/pages/AdminContent"));
const AdminBilling = lazy(() => import("../features/admin/pages/AdminBilling"));
const AdminWorkflows = lazy(
  () => import("../features/admin/pages/AdminWorkflows"),
);
const AdminSecurity = lazy(
  () => import("../features/admin/pages/AdminSecurity"),
);
const AdminSystem = lazy(() => import("../features/admin/pages/AdminSystem"));
const AdminBugReports = lazy(
  () => import("../features/admin/pages/AdminBugReports"),
);
const NotFound = lazy(() => import("./pages/NotFound"));

function RootHomeRoute() {
  const location = useLocation();
  const search = String(location.search || "").trim();
  if (
    search.includes("source=checkout") &&
    search.includes("enterprise_contract_id=")
  ) {
    return <Navigate to={`/auth/enterprise/claim${search}`} replace />;
  }
  return <PublicHomeRoute />;
}

export default function App() {
  return (
    <AppErrorBoundary>
      <Suspense fallback={<RouteLoadingFallback />}>
        <EntryVerificationGate>
          <PrelaunchGate>
            <Routes>
            {/* Public routes all share the PublicLayout */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<RootHomeRoute />} />
              <Route path="index.html" element={<Navigate to="/" replace />} />
              <Route path="features" element={<Features />} />
              <Route path="pricing" element={<Pricing />} />
              <Route path="why-refanno" element={<WhyRefanno />} />
              <Route path="meet-the-team" element={<MeetTheTeam />} />
              <Route path="contact" element={<Contact />} />
              <Route path="contact-sales" element={<ContactSales />} />
              <Route path="terms" element={<Terms />} />
              <Route path="privacy" element={<Privacy />} />
              <Route path="faq" element={<FAQ />} />
              <Route path="getting-started" element={<GettingStarted />} />
              <Route path="glossary" element={<Glossary />} />
              <Route
                path="early-access"
                element={<EarlyAccessQuestionnaire />}
              />

              <Route element={<AppDataShell />}>
                <Route path="auth" element={<AuthStart />} />
                <Route path="auth/callback" element={<AuthCallback />} />
                <Route path="auth/complete" element={<AuthComplete />} />
                <Route path="auth/login" element={<Login />} />
                <Route path="auth/magic-link" element={<MagicLinkLogin />} />
                <Route path="auth/signup" element={<Signup />} />
                <Route
                  path="auth/signup/complete"
                  element={<SignupComplete />}
                />
                <Route path="auth/reset-password" element={<ResetPassword />} />
                <Route path="auth/welcome" element={<Welcome />} />
                <Route
                  path="auth/enterprise/claim"
                  element={<EnterpriseCheckoutClaim />}
                />
                <Route path="auth/invite" element={<Invite />} />
                <Route path="auth/access-removed" element={<AccessRemoved />} />
                <Route path="auth/access-blocked" element={<AccessBlocked />} />
                <Route path="auth/sso-required" element={<SsoRequired />} />
                <Route
                  path="auth/email-change/pending"
                  element={<EmailChangePending />}
                />
                <Route
                  path="auth/email-pending"
                  element={<PendingEmailConfirmation />}
                />
                <Route
                  path="auth/invite/pending"
                  element={<PendingInvite />}
                />
                <Route path="reviewer/invite" element={<ExternalReviewerInvitePage />} />
              </Route>
            </Route>

            <Route element={<AppDataShell />}>
              <Route path="reviewer/context/:capabilityId" element={<ExternalReviewerWorkspace />} />
              <Route element={<PrivateAppShell />}>
                <Route path="admin" element={<AdminLayout />}>
                  <Route index element={<AdminOverview />} />
                  <Route
                    path="support-requests"
                    element={<AdminSupportRequests />}
                  />
                  <Route
                    path="contact-requests"
                    element={<AdminContactRequests />}
                  />
                  <Route
                    path="enterprise-contracts"
                    element={<AdminEnterpriseContracts />}
                  />
                  <Route
                    path="organizations"
                    element={<AdminOrganizations />}
                  />
                  <Route
                    path="organizations/:orgId"
                    element={<AdminOrganizationDetail />}
                  />
                  <Route
                    path="organizations/:orgId/security"
                    element={<AdminOrganizationSecurity />}
                  />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="users/:userId" element={<AdminUserDetail />} />
                  <Route path="teams" element={<AdminTeams />} />
                  <Route path="teams/:teamId" element={<AdminTeamDetail />} />
                  <Route path="content" element={<AdminContent />} />
                  <Route path="billing" element={<AdminBilling />} />
                  <Route path="workflows" element={<AdminWorkflows />} />
                  <Route path="security" element={<AdminSecurity />} />
                  <Route path="system" element={<AdminSystem />} />
                  <Route path="bug-reports" element={<AdminBugReports />} />
                </Route>

                {/* Internal routes (not nested under /dashboard) */}
                <Route element={<InternalLayout />}>
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="review" element={<ReviewWorkspace />} />
                  <Route path="reviewer/manage" element={<ExternalReviewerAdminPage />} />
                  <Route
                    path="inbox"
                    element={
                      <MessagingRouteGate>
                        <InboxPage />
                      </MessagingRouteGate>
                    }
                  />
                  <Route
                    path="inbox/:threadId"
                    element={
                      <MessagingRouteGate>
                        <InboxPage />
                      </MessagingRouteGate>
                    }
                  />
                  <Route path="/assets">
                    <Route index element={<Assets />} />
                    <Route path="approvals" element={<AssetApprovals />} />
                  </Route>
                  <Route path="portfolio" element={<Portfolio />} />
                  <Route
                    path="projects/:projectRef"
                    element={<ProjectDetail />}
                  />
                  <Route
                    path="reference-library"
                    element={<ReferenceLibrary />}
                  />
                  <Route path="brand-claims" element={<BrandClaims />} />
                  <Route
                    path="brand-claims/approvals"
                    element={<BrandClaimApprovals />}
                  />
                  <Route
                    path="brand-claims/new"
                    element={<BrandClaimBuilder />}
                  />
                  <Route
                    path="brand-claims/:claimId/edit"
                    element={<BrandClaimBuilder />}
                  />
                  <Route path="archive" element={<Archive />} />
                  <Route path="exports" element={<ExportsPage />} />
                  <Route path="change-history" element={<ChangeHistory />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="billing/portal" element={<BillingPortal />} />
                  <Route path="help" element={<Help />} />

                  <Route element={<FullscreenLayout />}>
                    <Route
                      path="/reference-library/viewer"
                      element={<Viewer />}
                    />
                    <Route
                      path="/assets/editor/:assetId"
                      element={<AssetEditor />}
                    />
                  </Route>
                </Route>
              </Route>
            </Route>

            <Route element={<LoggedOutLayout />}>
              <Route path="/auth/logout" element={<Logout />} />
            </Route>

            {/* catch-all 404 page */}
            <Route path="*" element={<NotFound />} />
            </Routes>
          </PrelaunchGate>
        </EntryVerificationGate>
      </Suspense>
    </AppErrorBoundary>
  );
}
